import React, { useEffect, useState } from "react";
import { useI18n } from "@/i18n";
import { useCart } from "@/state/CartContext";
import { fetchMyPromos } from "@/lib/api";
import { describePromo } from "@/lib/promoText";
import type { PersonalPromo } from "@/types";
import { haptic } from "@/lib/telegram";

/**
 * Promo code entry in the cart. Deliberately compact (discovery §6: the code
 * field must not make customers feel they overpay without one). Personal
 * codes issued by staff appear as a card with a one-tap "Apply".
 */
export function PromoBox({ error }: { error: string | null }) {
  const { t, locale } = useI18n();
  const { promoCode, setPromoCode } = useCart();
  const [draft, setDraft] = useState("");
  const [open, setOpen] = useState(false);
  const [personal, setPersonal] = useState<PersonalPromo[]>([]);

  useEffect(() => {
    fetchMyPromos()
      .then(setPersonal)
      .catch(() => setPersonal([]));
  }, []);

  const apply = (code: string) => {
    const clean = code.trim().toUpperCase();
    if (!clean) return;
    haptic("light");
    setPromoCode(clean);
    setDraft("");
    setOpen(false);
  };

  const unusedPersonal = personal.filter((p) => p.code !== promoCode);

  return (
    <div style={{ marginBottom: "var(--space-4)" }}>
      {unusedPersonal.map((p) => (
        <div key={p.code} className="card promo-personal">
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="promo-personal__title">{t("cart.personalTitle")}</div>
            <div className="promo-personal__desc">
              <code>{p.code}</code> · {describePromo(t, locale, p)}
            </div>
          </div>
          <button className="btn btn--ghost" onClick={() => apply(p.code)}>
            {t("cart.rewardApply")}
          </button>
        </div>
      ))}

      {promoCode ? (
        <div className={`promo-applied ${error ? "promo-applied--error" : ""}`}>
          <span>{error ?? t("cart.promoApplied", { code: promoCode })}</span>
          <button className="btn--danger-text btn" onClick={() => setPromoCode(null)}>
            {t("cart.promoRemove")}
          </button>
        </div>
      ) : open ? (
        <div style={{ display: "flex", gap: 8 }}>
          <input
            className="promo-input"
            autoFocus
            placeholder={t("cart.promoPlaceholder")}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && apply(draft)}
            autoCapitalize="characters"
          />
          <button className="btn btn--ghost" onClick={() => apply(draft)} disabled={!draft.trim()}>
            {t("cart.applyPromo")}
          </button>
        </div>
      ) : (
        <button className="promo-toggle" onClick={() => setOpen(true)}>
          + {t("cart.promoPlaceholder")}
        </button>
      )}
    </div>
  );
}
